(() => {
  // Liquid serializes line properties as pairs; the Ajax Cart API uses an object.
  const properties = item => Array.isArray(item.properties) ? Object.fromEntries(item.properties) : (item.properties || {});
  const tierConfigs = config => (config.tiers || [config]).map(tier => ({...tier, allTiers: config.tiers, declineKey: config.tiers ? `cc_gift_declined_${tier.id}` : 'cc_gift_declined'}));
  const marked = (item, config = {}) => config.tiers
    ? tierConfigs(config).some(tier => marked(item, tier))
    : ((!config.allTiers || item.variant_id === config.variantId) && (properties(item)._cc_free_gift === '1' ||
      (config.provider === 'eg' && config.ruleName && properties(item)._Gifted === config.ruleName)));
  const gifts = (cart, config) => (cart.items || []).filter(item => marked(item, config));
  // EG's cart attribute outlives its per-tab test session. It is not permission to add.
  function egContext(config, env = window, now = Date.now()) {
    if (config.tiers) {
      const contexts = tierConfigs(config).filter(tier => tier.enabled).map(tier => egContext(tier, env, now));
      return {allowed: contexts.some(c => c.allowed), test: contexts.some(c => c.test), pending: contexts.some(c => c.pending)};
    }
    const denied = {allowed: false, test: false, pending: false};
    try {
      const meta = env.document?.getElementById('easygift-rules');
      const published = JSON.parse(meta?.content || '[]');
      const session = JSON.parse(env.sessionStorage?.getItem('eg_test_mode') || 'null');
      const testRules = session && Date.parse(session.expiresAt) > now ? (session.rules || [session.rule]) : [];
      const testRule = testRules.find(rule => rule?.name === config.ruleName);
      const rule = testRule || published.find(rule => rule.name === config.ruleName && rule.active && !rule.testMode);
      const initialized = typeof env.EasyGift?.Events?.subscribe === 'function';
      if (!rule) return {...denied, pending: !initialized && (!meta || /[?&]egtest=/.test(env.location?.search || ''))};
      if (!initialized) return {...denied, pending: true};
      const geo = rule.targeting?.additionalCriteria?.geo;
      const unrestricted = (!rule.targeting?.type || rule.targeting.type === 'all') && !geo?.include?.length && !geo?.exclude?.length;
      const scheduled = !rule.schedule?.enabled || (Date.parse(rule.schedule.starts) < now && Date.parse(rule.schedule.ends) > now);
      const accepted = env.EasyGift.nonTargetingRules?.some(id => String(id) === String(rule._id));
      return {allowed: Boolean(unrestricted && scheduled && accepted), test: Boolean(testRule), pending: false};
    } catch {
      return denied;
    }
  }
  async function waitForProvider(config) {
    if (config.provider !== 'eg') return false;
    const start = Date.now();
    let waited = false;
    while (egContext(config).pending && Date.now() - start < 12000) {
      waited = true;
      await new Promise(resolve => setTimeout(resolve, 100));
    }
    return waited;
  }
  function threshold(config, rate) {
    if (config.provider !== 'eg' && config.country === 'GB') return config.currency === 'GBP' && config.gbp > 0 ? Math.round(config.gbp * 100) : 0;
    if (!(config.eur > 0) || config.baseCurrency !== 'EUR') return 0;
    if (config.currency === 'EUR') return Math.round(config.eur * 100);
    return Number.isFinite(rate) && rate > 0 ? Math.ceil(config.eur * 100 * rate) : 0;
  }
  function state(cart, config, rate) {
    if (config.tiers) {
      const states = tierConfigs(config).map(tier => ({...state(cart, tier, rate), config: tier}));
      return {tiers: states, active: states.some(t => t.active), eligible: states.some(t => t.eligible), lines: gifts(cart, config)};
    }
    const limit = threshold(config, rate);
    const total = (cart.items || []).filter(item => !marked(item, config.allTiers ? {tiers: config.allTiers} : config)).reduce((sum, item) => sum + item.original_line_price, 0);
    const active = Boolean(config.enabled && config.available && config.variantId && limit > 0 && (config.provider !== 'eg' || config.ruleName));
    const campaign = config.provider === 'eg' ? `eg:${config.ruleName}:${config.variantId}:${config.eur}` : `${config.variantId}:${config.eur}:${config.gbp}`;
    return {active, limit, total, campaign, eligible: active && total >= limit,
      declined: cart.attributes?.[config.declineKey || 'cc_gift_declined'] === campaign,
      lines: gifts(cart, config)};
  }
  function choiceAllowed(choice, current, config) {
    return choice === 'remove' ? current.lines.length > 0 :
      choice === 'add' && current.eligible && config.available && config.ruleName && current.lines.length === 0;
  }
  async function reconcile(cart, config, rate, request, options = {}) {
    if (config.tiers) {
      let changed = false;
      let failed = false;
      for (const tier of tierConfigs(config).sort((a, b) => a.eur - b.eur)) {
        const selected = String(options.choice?.tier) === String(tier.id);
        const result = await reconcile(cart, tier, rate, request, {...options, choice: selected ? options.choice.action : undefined});
        cart = result.cart;
        changed ||= result.changed;
        failed ||= result.failed;
      }
      // Adding a later tier can make EG restore an earlier, declined gift.
      // Enforce every removal preference once more after all additions settle.
      const declinedLines = () => state(cart, config, rate).tiers.filter(tier => tier.declined).flatMap(tier => tier.lines);
      const unwanted = declinedLines();
      if (unwanted.length) {
        cart = await request('update.js', {updates: Object.fromEntries(unwanted.map(item => [item.key, 0]))});
        changed = true;
        if (declinedLines().length) throw new Error(config.error || 'Unable to remove your declined free gift.');
      }
      return {cart, changed, failed, state: state(cart, config, rate)};
    }

    // Provider permission and theme spend eligibility must both allow an addition.
    if (config.provider === 'eg') {
      let changed = false;
      let current = state(cart, config, rate);
      if (options.choice) {
        cart = await request('update.js', {
          attributes: {[config.declineKey || 'cc_gift_declined']: options.choice === 'remove' ? current.campaign : ''},
          ...(options.choice === 'remove' ? {updates: Object.fromEntries(current.lines.map(item => [item.key, 0]))} : {}),
        });
        changed = true;
        current = state(cart, config, rate);
      }
      const provider = egContext(config);
      if ((!current.eligible || !config.available || current.declined) && current.lines.length) {
        cart = await request('update.js', {updates: Object.fromEntries(current.lines.map(item => [item.key, 0]))});
        changed = true;
      } else if (provider.allowed && (choiceAllowed(options.choice, current, config) || (!options.skipAdd && !current.declined && choiceAllowed('add', current, config)))) {
        cart = await request('add.js', {items: [{id: config.variantId, quantity: 1, properties: {
          _Gifted: config.ruleName,
          ...(provider.test ? {_eg_test: 'true'} : {})
        }}]});
        changed = true;
      }
      let remaining = gifts(cart, config);
      // If EG added concurrently, retain only one unit of the selected reward.
      const retained = remaining.find(item => item.variant_id === config.variantId);
      const updates = {};
      for (const item of remaining) {
        if (item !== retained) updates[item.key] = 0;
        else if (item.quantity !== 1) updates[item.key] = 1;
      }
      if (Object.keys(updates).length) {
        cart = await request('update.js', {updates});
        changed = true;
        remaining = gifts(cart, config);
      }
      const paidGifts = remaining.filter(item => item.final_line_price !== 0);
      if (paidGifts.length) {
        cart = await request('update.js', {updates: Object.fromEntries(paidGifts.map(item => [item.key, 0]))});
        changed = true;
        remaining = gifts(cart, config);
        if (remaining.some(item => item.final_line_price !== 0)) throw new Error(config.error || 'Unable to remove an undiscounted gift.');
      }
      if (cart.attributes?.[config.declineKey || 'cc_gift_declined'] && !(cart.items || []).some(item => !marked(item, config))) {
        cart = await request('update.js', {attributes: {[config.declineKey || 'cc_gift_declined']: ''}});
        changed = true;
      }
      const finalState = state(cart, config, rate);
      remaining = finalState.lines;
      if (remaining.length && (!finalState.eligible || !config.available || finalState.declined || remaining.length !== 1 || remaining[0].variant_id !== config.variantId || remaining[0].quantity !== 1 || remaining[0].final_line_price !== 0)) {
        throw new Error(config.error || 'Unable to update your free gift.');
      }
      // Never leave an undiscounted promotional item in the customer’s cart.
      // Surface an unsuccessful removal instead of fighting EG with a retry loop.
      const failed = (state(cart, config, rate).declined && remaining.length > 0) ||
        paidGifts.length > 0;
      return {cart, changed, failed, state: state(cart, config, rate)};
    }
    let changed = false;
    let failed = false;
    const update = async (endpoint, body) => { cart = await request(endpoint, body); changed = true; };
    let current = state(cart, config, rate);
    if (options.choice) {
      await update('update.js', {attributes: {[config.declineKey || 'cc_gift_declined']: options.choice === 'remove' ? current.campaign : ''}});
      current = state(cart, config, rate);
    }
    const wanted = current.eligible && config.available && !current.declined;
    if (current.lines.length && (!wanted || current.lines.length !== 1 || current.lines[0].variant_id !== config.variantId || current.lines[0].quantity !== 1 || current.lines[0].final_line_price !== 0)) {
      await update('update.js', {updates: Object.fromEntries(current.lines.map(item => [item.key, 0]))});
    }
    if (wanted && !gifts(cart).length && !options.skipAdd) {
      try {
        await update('add.js', {items: [{id: config.variantId, quantity: 1, properties: {_cc_free_gift: '1'}}]});
        const added = gifts(cart);
        if (added.length !== 1 || added[0].variant_id !== config.variantId || added[0].quantity !== 1 || added[0].final_line_price !== 0) {
          failed = true;
          if (added.length) await update('update.js', {updates: Object.fromEntries(added.map(item => [item.key, 0]))});
        }
      } catch (error) {
        error.giftMutation = true;
        throw error;
      }
    }
    return {cart, changed, failed, state: state(cart, config, rate)};
  }
  function rewardProgress(giftTotal, giftLimit, shippingTotal, shippingLimit) {
    const maximum = Math.max(giftLimit, shippingLimit);
    if (!(maximum > 0)) return 0;
    const giftPosition = giftLimit / maximum * 100;
    const shippingPosition = shippingLimit / maximum * 100;
    const positions = [{position: 0, total: 0},
      {position: giftPosition, total: giftTotal, limit: giftLimit}];
    if (shippingLimit) positions.push({position: shippingPosition, total: shippingTotal, limit: shippingLimit});
    positions.sort((a, b) => a.position - b.position);
    let progress = 0;
    for (let index = 1; index < positions.length; index++) {
      const milestone = positions[index];
      if (milestone.total >= milestone.limit) progress = milestone.position;
      else {
        const previous = positions[index - 1].position;
        const lower = previous / 100 * maximum;
        const fraction = Math.max(0, Math.min(1, (milestone.total - lower) / (milestone.limit - lower || 1)));
        progress = Math.max(progress, previous + (milestone.position - previous) * fraction);
        break;
      }
    }
    return progress;
  }
  function render(root, cart, config, rate, failed, money) {
    if (typeof document !== 'undefined' && document.dispatchEvent && typeof CustomEvent !== 'undefined') {
      const bannerShipping = root?.querySelector('[data-cc-shipping]');
      const detail = {cart, config, rate, failed, money, shippingLimit: Number(bannerShipping?.dataset.shippingThreshold) || 0, shippingTotal: Number(bannerShipping?.dataset.shippingTotal) || 0};
      window.ccRewardsSnapshot = detail;
      document.dispatchEvent(new CustomEvent('cc:rewards-update', {detail}));
    }
    if (config.tiers) return renderTiers(root, cart, config, rate, failed, money);
    const block = root?.querySelector('[data-cc-gift]');
    if (!block) return;
    const current = state(cart, config, rate);
    const offered = current.active && (config.provider !== 'eg' || egContext(config).allowed);
    block.hidden = !offered;
    root.classList.toggle('cc-cart--combined-rewards', offered);
    if (!offered) return;
    const included = current.lines.length === 1 && current.lines[0].variant_id === config.variantId && current.lines[0].final_line_price === 0;
    const unavailable = (failed && current.eligible) || !config.available;
    const message = block.querySelector('[data-gift-message]');
    const giftMessage = unavailable ? message.dataset.unavailable : current.eligible ? message.dataset.unlocked : message.dataset.locked;
    const shipping = root.querySelector('[data-cc-shipping]');
    const shippingLimit = Number(shipping?.dataset.shippingThreshold) || 0;
    const shippingTotal = Number(shipping?.dataset.shippingTotal) || 0;
    const maximum = Math.max(current.limit, shippingLimit);
    // Place each milestone on one scale while preserving its eligibility basis.
    const giftPosition = current.limit / maximum * 100;
    const shippingPosition = shippingLimit / maximum * 100;
    const shippingUnlocked = shippingLimit > 0 && shippingTotal >= shippingLimit;
    // Show only the next reward's prompt, keeping its amount semantically bold.
    message.replaceChildren();
    if (shippingLimit && !shippingUnlocked) {
      const shippingMessage = shipping.querySelector('p');
      if (shippingMessage) message.append(...Array.from(shippingMessage.childNodes, node => node.cloneNode(true)));
    } else {
      const parts = giftMessage.split('[amount]');
      message.append(parts[0]);
      if (parts.length > 1) {
        const amount = document.createElement('strong');
        amount.textContent = money(Math.max(0, current.limit - current.total));
        message.append(amount, parts.slice(1).join('[amount]'));
      }
    }
    const progress = rewardProgress(current.total, current.limit, shippingTotal, shippingLimit);
    const shippingMarker = block.querySelector('[data-shipping-marker]');
    shippingMarker.hidden = !shippingLimit;
    shippingMarker.style.left = `${shippingPosition}%`;
    shippingMarker.classList.toggle('is-unlocked', shippingUnlocked);
    shippingMarker.setAttribute('title', shippingUnlocked ? 'Free shipping unlocked' : 'Free shipping');
    let shippingStatus = block.querySelector('[data-reward-shipping-status]');
    if (!shippingStatus) {
      shippingStatus = document.createElement('span');
      shippingStatus.setAttribute('data-reward-shipping-status', '');
      shippingStatus.className = 'cc-cart-gift__shipping-status';
      shippingStatus.textContent = shipping?.dataset.successMessage || 'Free shipping unlocked 🎉';
      message.before(shippingStatus);
    }
    shippingStatus.hidden = !shippingUnlocked;
    block.querySelector('[data-gift-marker]').style.left = `${giftPosition}%`;
    block.querySelector('[data-gift-marker]').setAttribute('title', giftMessage.replace('[amount]', money(Math.max(0, current.limit - current.total))));
    block.querySelector('[data-gift-progress]').setAttribute('aria-valuenow', String(Math.floor(progress)));
    block.querySelector('[data-gift-progress]').setAttribute('aria-label', shippingLimit ? 'Free shipping and gift progress' : 'Free gift progress');
    block.querySelector('[data-gift-fill]').style.width = `${progress}%`;
    block.classList.toggle('is-unlocked', current.eligible && !unavailable);
    const card = block.querySelector('[data-gift-card]');
    card.hidden = !current.eligible || unavailable;
    const status = block.querySelector('[data-gift-status]');
    status.textContent = included ? status.dataset.included : status.dataset.removed;
    const value = card.querySelector('[data-gift-value]');
    if (value) {
      value.hidden = !included;
      status.hidden = included;
    }
    block.querySelector('[data-cc-gift-action="remove"]').hidden = !included;
    block.querySelector('[data-cc-gift-action="add"]').hidden = included;
  }
  function renderTiers(root, cart, config, rate, failed, money) {
    const block = root?.querySelector('[data-cc-gift]');
    if (!block) return;
    const tiers = state(cart, config, rate).tiers.filter(tier => tier.active && egContext(tier.config).allowed).sort((a, b) => a.limit - b.limit);
    block.hidden = !tiers.length;
    root.classList.toggle('cc-cart--combined-rewards', Boolean(tiers.length));
    block.querySelectorAll('[data-gift-card], [data-gift-marker]').forEach(el => { el.hidden = true; });
    if (!tiers.length) return;
    const shipping = root.querySelector('[data-cc-shipping]');
    const shippingLimit = Number(shipping?.dataset.shippingThreshold) || 0;
    const shippingTotal = Number(shipping?.dataset.shippingTotal) || 0;
    const shippingUnlocked = shippingLimit > 0 && shippingTotal >= shippingLimit;
    const maximum = Math.max(shippingLimit, ...tiers.map(tier => tier.limit));
    const sharedTier = tiers.find(tier => tier.limit === shippingLimit);
    const sharedUnlocked = sharedTier?.eligible && shippingUnlocked && !failed;
    const next = tiers.find(tier => !tier.eligible) || tiers[tiers.length - 1];
    const message = block.querySelector('[data-gift-message]');
    message.replaceChildren();
    if (shippingLimit && !shippingUnlocked && (shippingLimit < next.limit || next.eligible)) {
      const text = shipping.querySelector('p');
      if (text) message.append(...Array.from(text.childNodes, node => node.cloneNode(true)));
    } else {
      const card = block.querySelector(`[data-gift-card][data-gift-tier="${next.config.id}"]`);
      const sharedLocked = sharedTier === next && !shippingUnlocked && !next.eligible;
      const text = failed && next.eligible ? card.dataset.unavailable : next.eligible ? card.dataset.unlocked : sharedLocked ? card.dataset.shippingLocked : card.dataset.locked;
      const parts = text.split('[amount]');
      message.append(parts[0]);
      if (parts.length > 1) {
        const amount = document.createElement('strong');
        amount.textContent = money(Math.max(0, next.limit - next.total, sharedLocked ? shippingLimit - shippingTotal : 0));
        message.append(amount, parts.slice(1).join('[amount]'));
      }
    }
    for (const tier of tiers) {
      const marker = block.querySelector(`[data-gift-marker="${tier.config.id}"]`);
      marker.hidden = false;
      // The gift icon represents both rewards when their thresholds match.
      marker.style.left = `${tier.limit / maximum * 100}%`;
      marker.classList.toggle('is-unlocked', tier.eligible);
      const card = block.querySelector(`[data-gift-card][data-gift-tier="${tier.config.id}"]`);
      marker.title = (tier === sharedTier && sharedUnlocked ? card.dataset.shippingUnlocked : tier === sharedTier && !tier.eligible && !shippingUnlocked ? card.dataset.shippingLocked : tier.eligible ? card.dataset.unlocked : card.dataset.locked).replace('[amount]', money(Math.max(0, tier.limit - tier.total)));
      const included = tier.lines.length === 1 && tier.lines[0].variant_id === tier.config.variantId && tier.lines[0].final_line_price === 0;
      card.hidden = !included && (!tier.eligible || failed);
      const status = card.querySelector('[data-gift-status]');
      status.textContent = included ? status.dataset.included : status.dataset.removed;
    const value = card.querySelector('[data-gift-value]');
    if (value) {
      value.hidden = !included;
      status.hidden = included;
    }
      card.querySelector('[data-cc-gift-action="remove"]').hidden = !included;
      card.querySelector('[data-cc-gift-action="add"]').hidden = included;
    }
    const progress = rewardProgress(tiers[0].total, Math.max(...tiers.map(t => t.limit)), shippingTotal, shippingLimit);
    const shippingMarker = block.querySelector('[data-shipping-marker]');
    shippingMarker.hidden = !shippingLimit || Boolean(sharedTier);
    shippingMarker.style.left = `${shippingLimit / maximum * 100}%`;
    shippingMarker.classList.toggle('is-unlocked', shippingUnlocked);
    shippingMarker.title = shippingUnlocked ? 'Free shipping unlocked' : 'Free shipping';
    let shippingStatus = block.querySelector('[data-reward-shipping-status]');
    if (!shippingStatus) {
      shippingStatus = document.createElement('span');
      shippingStatus.setAttribute('data-reward-shipping-status', '');
      shippingStatus.className = 'cc-cart-gift__shipping-status';
      message.before(shippingStatus);
    }
    const unlockedTiers = failed ? [] : tiers.filter(tier => tier.eligible);
    const unlockedCount = unlockedTiers.length;
    const singleCard = unlockedCount === 1 && block.querySelector(`[data-gift-card][data-gift-tier="${unlockedTiers[0].config.id}"]`);
    shippingStatus.textContent = unlockedCount >= 2
      ? block.dataset.allShippingUnlocked.replace('[count]', String(unlockedCount))
      : singleCard ? singleCard.dataset.shippingUnlocked : shipping?.dataset.successMessage || 'Free shipping unlocked 🎉';
    message.hidden = Boolean(shippingUnlocked && unlockedCount > 0 && next.eligible);
    shippingStatus.hidden = !shippingUnlocked;
    block.querySelector('[data-gift-progress]').setAttribute('aria-valuenow', String(Math.floor(progress)));
    block.querySelector('[data-gift-fill]').style.width = `${progress}%`;
    block.classList.toggle('is-unlocked', tiers.every(t => t.eligible) && !failed);
  }
  function includedKeys(cart, config, rate) {
    return new Set(tierConfigs(config).flatMap(tier => {
      const current = state(cart, tier, rate);
      return current.eligible && egContext(tier).allowed ? current.lines.filter(item => item.variant_id === tier.variantId && item.final_line_price === 0).map(item => item.key) : [];
    }));
  }
  window.CurlyCoGift = {includedKeys, state, gifts, threshold, reconcile, render, rewardProgress, egContext, waitForProvider};
})();
